import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Alert, Button, Card, useToast } from '@kss/ui';
import { apiClient } from '@kss/api';
import { postStockMovements, resolveDefaultWarehouseId } from '../../utils/inventory';
import { AuthImg } from '../../components/AuthImg';
import {
  Bike, ChevronDown, ChevronRight, Coffee, Maximize2, Minus, Package, Plus, Printer, RefreshCw, Send, ShoppingBag, Trash2, Users, X,
} from 'lucide-react';
import { printKot, printInvoiceByFormat, type InvoicePrintInput, type TenantHeader } from '../../print/templates';
import { getAssignedFormat } from '../../print/formats';
import { getTenantHeader, getTenantHeaderInlined, getPrintBrandingInlined, printQrUrl } from '../../print/branding';

/**
 * Restaurant floor view — live table-status grid + per-table order
 * panel that pushes line items to the kitchen as a KOT and turns the
 * order into a printable bill on close.
 *
 * Layout:
 *   [ table grid ............................ ] [ order panel  ]
 *   [ grouped by zone / area                  ] [  - lines     ]
 *   [ status colours: free/occupied/reserved  ] [  - send KOT  ]
 *                                                [  - print bill]
 */

type TableStatus = 'Available' | 'Occupied' | 'Reserved' | 'Billed' | 'Closed';
type OrderType = 'DineIn' | 'Takeaway' | 'Delivery';

interface RestaurantTable extends Record<string, unknown> {
  restaurantTableId?: string;
  id?: number | string;
  code?: string;
  name?: string;
  zone?: string;
  capacity?: number;
  status?: string;
}

interface MenuItem extends Record<string, unknown> {
  menuItemId?: string;
  id?: number | string;
  code?: string;
  name?: string;
  price?: number;
  sellingPrice?: number;
  menuCategoryId?: string;
  taxRate?: number;
}

interface MenuCategory extends Record<string, unknown> {
  menuCategoryId?: string;
  id?: number | string;
  name?: string;
}

interface OrderLine {
  menuItemId: string | number;
  name: string;
  qty: number;
  rate: number;
  taxRate: number;
  /** True until "Send to KOT" — set false after the kitchen has the order. */
  unsent: boolean;
  notes?: string;
}

interface Order {
  tableId: string | number | null;
  type: OrderType;
  guests: number;
  customerName?: string;
  lines: OrderLine[];
  /** RestaurantOrder.id once the order is persisted to the API (so the
   *  kitchen/table-board pick it up and billing can stamp it). */
  restaurantOrderId?: number | string;
  /** SalesInvoice.id once a bill has been generated. */
  salesInvoiceId?: number | string;
}

const emptyOrder = (tableId: string | number | null = null): Order => ({
  tableId,
  type: 'DineIn',
  guests: 2,
  lines: [],
});

/** Pure totals for ANY order (used by the per-table cards, not just active). */
function orderTotals(order: Order | null | undefined) {
  if (!order) return { subtotal: 0, tax: 0, grand: 0, items: 0 };
  let subtotal = 0;
  let tax = 0;
  for (const l of order.lines) {
    const taxable = l.qty * l.rate;
    subtotal += taxable;
    tax += taxable * (l.taxRate / 100);
  }
  return {
    subtotal,
    tax,
    grand: Math.round(subtotal + tax),
    items: order.lines.reduce((n, l) => n + l.qty, 0),
  };
}

export default function RestaurantFloor() {
  const toast = useToast();

  const [tables, setTables] = useState<RestaurantTable[]>([]);
  const [tableStatus, setTableStatus] = useState<Record<string, TableStatus>>({});
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [tenant, setTenant] = useState<TenantHeader>({ name: 'KSS BillBook' });
  const [activeCat, setActiveCat] = useState<string | 'all'>('all');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // One open order per table (kept client-side; persisted to API on send/close).
  const [orders, setOrders] = useState<Record<string, Order>>({});
  const [activeTableId, setActiveTableId] = useState<string | null>(null);
  // Full-screen order overlay (admin: see the whole order list, scrollable).
  const [orderFull, setOrderFull] = useState(false);
  // Per-table card collapse in the multi-table panel (the active table is the
  // editor; other open tables show as collapsible cards with their items).
  const [minimized, setMinimized] = useState<Record<string, boolean>>({});
  const toggleMin = (id: string) => setMinimized((m) => ({ ...m, [id]: !m[id] }));

  const activeOrder = activeTableId ? orders[activeTableId] : null;

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const [t, mi, mc] = await Promise.all([
        apiClient.get('/restaurant/RestaurantTable').then(pickList<RestaurantTable>).catch(() => []),
        apiClient.get('/restaurant/MenuItem').then(pickList<MenuItem>).catch(() => []),
        apiClient.get('/restaurant/MenuCategory').then(pickList<MenuCategory>).catch(() => []),
      ]);
      setTables(t);
      setMenuItems(mi);
      setCategories(mc);
      // No backend tenant endpoint (`/tenants/Tenant/me` → 404, gap G86) — read
      // the print header from the admin-set branding (Settings → Print Formats).
      setTenant(getTenantHeader());
      // Hydrate status from each row's `status` column when present.
      const init: Record<string, TableStatus> = {};
      for (const r of t) {
        const id = String(r.restaurantTableId ?? r.id ?? '');
        if (!id) continue;
        init[id] = normaliseStatus(String(r.status ?? 'Available'));
      }
      setTableStatus(init);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load floor');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  // KPI counts
  const counts = useMemo(() => {
    const c = { Available: 0, Occupied: 0, Reserved: 0, Billed: 0, Closed: 0 };
    for (const id of Object.keys(tableStatus)) {
      c[tableStatus[id] ?? 'Available']++;
    }
    return c;
  }, [tableStatus]);

  // Tables grouped by zone (or "Main" if none)
  const tablesByZone = useMemo(() => {
    const m: Record<string, RestaurantTable[]> = {};
    for (const t of tables) {
      const z = String(t.zone ?? 'Main');
      (m[z] ??= []).push(t);
    }
    return m;
  }, [tables]);

  function selectTable(t: RestaurantTable) {
    const id = String(t.restaurantTableId ?? t.id ?? '');
    if (!id) return;
    setActiveTableId(id);
    setOrders((cur) => {
      if (cur[id]) return cur;
      return { ...cur, [id]: emptyOrder(id) };
    });
    if ((tableStatus[id] ?? 'Available') === 'Available') {
      setTableStatus((s) => ({ ...s, [id]: 'Occupied' }));
    }
  }

  function updateOrder(patch: Partial<Order>) {
    if (!activeTableId) return;
    setOrders((cur) => ({
      ...cur,
      [activeTableId]: { ...(cur[activeTableId] ?? emptyOrder(activeTableId)), ...patch },
    }));
  }

  function addItem(mi: MenuItem) {
    if (!activeTableId) return;
    const id = (mi.menuItemId ?? mi.id) as string | number | undefined;
    if (id == null) return;
    const order = orders[activeTableId] ?? emptyOrder(activeTableId);
    const existingIdx = order.lines.findIndex(
      (l) => l.menuItemId === id && l.unsent,
    );
    let nextLines: OrderLine[];
    if (existingIdx >= 0) {
      nextLines = order.lines.map((l, i) =>
        i === existingIdx ? { ...l, qty: l.qty + 1 } : l,
      );
    } else {
      nextLines = [
        ...order.lines,
        {
          menuItemId: id,
          name: mi.name ?? mi.code ?? 'Item',
          qty: 1,
          rate: Number(mi.price ?? mi.sellingPrice ?? 0),
          taxRate: Number(mi.taxRate ?? 5),
          unsent: true,
        },
      ];
    }
    updateOrder({ lines: nextLines });
  }

  function bumpLine(idx: number, delta: number) {
    if (!activeOrder) return;
    // Clamp at qty 1 — decrementing must NOT silently drop the line (that's
    // what the explicit Trash button is for). Mirrors the POS cart behaviour.
    const lines = activeOrder.lines.map((l, i) => (i === idx ? { ...l, qty: Math.max(1, l.qty + delta) } : l));
    updateOrder({ lines });
  }

  function removeLine(idx: number) {
    if (!activeOrder) return;
    const l = activeOrder.lines[idx];
    if (!window.confirm(`Remove ${l?.name ?? 'this item'} from the order?`)) return;
    updateOrder({ lines: activeOrder.lines.filter((_, i) => i !== idx) });
  }

  const totals = useMemo(() => {
    if (!activeOrder) return { subtotal: 0, tax: 0, grand: 0, items: 0 };
    let subtotal = 0;
    let tax = 0;
    for (const l of activeOrder.lines) {
      const taxable = l.qty * l.rate;
      subtotal += taxable;
      tax += taxable * (l.taxRate / 100);
    }
    return {
      subtotal,
      tax,
      grand: Math.round(subtotal + tax),
      items: activeOrder.lines.reduce((n, l) => n + l.qty, 0),
    };
  }, [activeOrder]);

  /** Resolve the numeric table id + display label for the active table. */
  function activeTableInfo() {
    const row = tables.find((t) => String(t.restaurantTableId ?? t.id) === activeTableId);
    const tableId = (row?.id ?? row?.restaurantTableId ?? activeTableId) as number | string;
    const tableCode = String(row?.name ?? row?.code ?? activeTableId ?? '').slice(0, 24);
    return { tableId, tableCode };
  }

  /**
   * Persist (create or update) the table's RestaurantOrder so it shows on
   * the Kitchen / Waiter / Table-Status boards. The order's items go into
   * `description` JSON in the exact shape those boards parse
   * ({ tableId, tableCode, customerName, items:[{name,quantity,note}] }),
   * and the workflow `status` is written as a canonical string.
   * Returns the order id.
   */
  async function persistOrder(workflowStatus: string): Promise<number | string | undefined> {
    if (!activeOrder || !activeTableId) return undefined;
    const { tableId, tableCode } = activeTableInfo();
    const description = JSON.stringify({
      tableId: String(tableId),
      tableCode,
      customerName: activeOrder.customerName ?? '',
      orderNote: '',
      placedAt: new Date().toISOString(),
      items: activeOrder.lines
        .filter((l) => l.qty > 0)
        .map((l) => ({ name: l.name, quantity: l.qty, note: l.notes ?? '' })),
      lines: activeOrder.lines
        .filter((l) => l.qty > 0)
        .map((l) => ({ menuItemId: l.menuItemId, name: l.name, quantity: l.qty, unitPrice: l.rate, taxRate: l.taxRate })),
      subtotal: round2(totals.subtotal),
      taxAmount: round2(totals.tax),
      total: totals.grand,
    });
    const code = activeOrder.restaurantOrderId
      ? undefined
      : `RO-${String(tableCode).replace(/\s+/g, '')}-${new Date().toISOString().slice(11, 19).replace(/:/g, '')}`;
    const body: Record<string, unknown> = {
      name: `${tableCode} — ${activeOrder.customerName || 'Walk-in'}`,
      description,
      // `status` is the NUMERIC row enum (0=Active) — sending a string
      // like "Cooking" 400s with invalid_body. The workflow stage lives
      // in the string `orderStatus` column (what the kitchen/board read).
      status: 0,
      orderStatus: workflowStatus,
      tableId,
      customerName: activeOrder.customerName ?? '',
      subtotal: round2(totals.subtotal),
      taxAmount: round2(totals.tax),
      grandTotal: totals.grand,
    };
    try {
      if (activeOrder.restaurantOrderId) {
        await apiClient.put('/restaurant/RestaurantOrder', { ...body, id: activeOrder.restaurantOrderId });
        return activeOrder.restaurantOrderId;
      }
      const r = await apiClient.post('/restaurant/RestaurantOrder', { code, ...body });
      const saved = (r.data?.data ?? r.data) as Record<string, unknown>;
      const id = (saved?.id as number) ?? undefined;
      if (id != null) updateOrder({ restaurantOrderId: id });
      return id;
    } catch (e) {
      console.warn('RestaurantOrder persist failed:', e);
      return activeOrder.restaurantOrderId;
    }
  }

  async function sendToKot() {
    if (!activeOrder || !activeTableId) return;
    const unsentLines = activeOrder.lines.filter((l) => l.unsent && l.qty > 0);
    if (unsentLines.length === 0) {
      toast.error('Nothing to send', 'All items are already with the kitchen.');
      return;
    }
    const kotTime = new Date().toISOString();
    const { tableId } = activeTableInfo();
    // 1. Persist the order so the kitchen/table boards see it (status
    //    'Cooking' = a kitchen-open state the KDS displays).
    const orderId = await persistOrder('Cooking');
    let kotNumber: string | undefined;
    try {
      const resp = await apiClient.post('/restaurant/Kot', {
        restaurantOrderId: orderId,
        tableId,
        station: 'main-kitchen',
        // schema column is `issuedAt`, not `kotTime` (kotTime was ignored).
        issuedAt: kotTime,
        status: 0,
        kotStatus: 'Sent',
        // Recoverable fallback: the kitchen board can render items from here
        // even if the SEPARATE KotItem POSTs below fail (transient 401).
        description: JSON.stringify({
          items: unsentLines.map((l) => ({ name: l.name, quantity: Number(l.qty) || 1, note: l.notes ?? '' })),
        }),
      });
      const saved = ((resp.data as { data?: unknown })?.data ?? resp.data) as Record<string, unknown>;
      const kotId = (saved?.id as number | string | undefined);
      kotNumber = (saved?.kotNumber as string) ?? (saved?.code as string) ?? undefined;
      // KOT items live in the SEPARATE `/restaurant/KotItem` table keyed by
      // `kotId` — a nested `items` array on the Kot is ignored by the backend.
      if (kotId != null) {
        await Promise.all(unsentLines.map((l, i) =>
          apiClient.post('/restaurant/KotItem', {
            code: `${saved?.code ?? kotNumber ?? 'KOT'}-L${i + 1}`,
            name: l.name,
            status: 0,
            kotId,
            menuItemId: l.menuItemId ?? null,
            itemName: l.name,
            quantity: Number(l.qty) || 1,
            modifierNote: l.notes ?? '',
            itemStatus: 'Pending',
          }).catch(() => undefined),
        ));
      }
      toast.success('KOT sent', `${unsentLines.length} item(s) to the kitchen`);
    } catch (e) {
      toast.error('KOT failed', e instanceof Error ? e.message : 'Network error');
      // Continue — flag locally so the cashier can retry.
    }
    // Print the kitchen slip regardless of API success so the kitchen
    // doesn't go silent when the network blips.
    const tableLabel =
      tables.find((t) => String(t.restaurantTableId ?? t.id) === activeTableId)?.name ??
      String(activeTableId).slice(0, 6);
    printKot({
      tableLabel: String(tableLabel),
      orderType: activeOrder.type,
      guests: activeOrder.guests,
      customerName: activeOrder.customerName,
      kotNumber,
      kotTime,
      items: unsentLines.map((l) => ({ name: l.name, qty: l.qty, notes: l.notes })),
      tenant,
    });
    // Mark sent locally regardless so the strikethrough updates.
    updateOrder({
      lines: activeOrder.lines.map((l) => (l.unsent ? { ...l, unsent: false } : l)),
    });
  }

  async function printBill() {
    if (!activeOrder || !activeTableId) return;
    if (activeOrder.lines.length === 0) {
      toast.error('Empty order', 'Add at least one item before printing.');
      return;
    }
    const { tableCode } = activeTableInfo();
    // 1. Ensure the order is persisted, then flip it to 'Billed'.
    const orderId = await persistOrder('Billed');

    // 2. Generate a SalesInvoice so the bill lands in Sales & Billing →
    //    Sales Invoices (with the Dine-in type). Mirrors the customer
    //    dine-out billing helper.
    try {
      const now = new Date();
      const invCode = `DIN-${now.toISOString().slice(2, 10).replace(/-/g, '')}-${now.toISOString().slice(11, 19).replace(/:/g, '')}`;
      const invDescription = JSON.stringify({
        source: 'Dine-in',
        tableCode,
        customerName: activeOrder.customerName ?? '',
        restaurantOrderId: orderId,
        lines: activeOrder.lines.filter((l) => l.qty > 0).map((l) => ({
          name: l.name, quantity: l.qty, unitPrice: l.rate, taxRate: l.taxRate,
          lineTotal: round2(l.qty * l.rate * (1 + l.taxRate / 100)),
        })),
        subTotal: round2(totals.subtotal),
        taxAmount: round2(totals.tax),
        grandTotal: totals.grand,
        issuedAt: now.toISOString(),
      });
      const inv = await apiClient.post('/sales/SalesInvoice', {
        code: invCode,
        name: `Dine-in ${tableCode}`,
        invoiceNumber: invCode,
        invoiceDate: now.toISOString(),
        customerName: activeOrder.customerName || 'Walk-in',
        currency: 'INR',
        invoiceType: 'Dine-in',
        invoiceStatus: 'Posted',
        paymentStatus: 'Unpaid',
        description: invDescription,
        subTotal: round2(totals.subtotal),
        taxAmount: round2(totals.tax),
        grandTotal: totals.grand,
        paidAmount: 0,
        balance: totals.grand,
        status: 0,
      });
      const savedInv = (inv.data?.data ?? inv.data) as Record<string, unknown>;
      const invId = (savedInv?.id as number) ?? undefined;
      if (invId != null) {
        // Persist real SalesInvoiceLine rows (keyed by salesInvoiceId) so
        // the invoice shows its products when opened in the Sales Invoice
        // editor — not just the description-JSON stopgap.
        const billLines = activeOrder.lines.filter((l) => l.qty > 0);
        await Promise.all(billLines.map((l, i) =>
          apiClient.post('/sales/SalesInvoiceLine', {
            code: `${invCode}-L${i + 1}`,
            name: l.name || `Line ${i + 1}`,
            status: 0,
            salesInvoiceId: invId,
            lineNumber: i + 1,
            productId: l.menuItemId ?? null,
            productName: l.name,
            quantity: l.qty,
            unitPrice: l.rate,
            taxRate: l.taxRate,
            taxableAmount: round2(l.qty * l.rate),
            lineTotal: round2(l.qty * l.rate * (1 + l.taxRate / 100)),
          }).catch(() => undefined),
        ));
        // Restaurant sale → stock OUT. Uses menuItemId as the product ref to
        // match the invoice line above. NOTE: if menu items aren't backed by
        // inventory-tracked products, map them to products / a recipe (BOM)
        // for accurate ingredient depletion — see BACKEND_GAPS.
        try {
          const warehouseId = await resolveDefaultWarehouseId();
          await postStockMovements({
            direction: 'Out',
            sourceType: 'Restaurant',
            sourceId: invId,
            docNumber: String(invCode ?? invId),
            warehouseId,
            lines: billLines.map((l) => ({ productId: l.menuItemId ?? null, quantity: Number(l.qty) || 0, unitCost: Number(l.rate) || 0, unitOfMeasure: 'PCS' })),
          });
        } catch { /* best-effort — never block the bill */ }
        updateOrder({ salesInvoiceId: invId });
        // Stamp the order with its invoice id (remarks) for traceability.
        if (orderId != null) {
          try {
            await apiClient.put('/restaurant/RestaurantOrder', {
              id: orderId, status: 0, orderStatus: 'Billed',
              salesInvoiceId: invId, remarks: `invoiceId=${invId}`,
            });
          } catch { /* best-effort */ }
        }
      }
      toast.success('Bill generated', `Invoice ${invCode} created`);
    } catch (e) {
      toast.error('Bill save failed', e instanceof Error ? e.message : 'Network error');
      // Non-blocking — still print locally.
    }

    setTableStatus((s) => ({ ...s, [activeTableId]: 'Billed' }));
    // Honor the admin-assigned Restaurant-bill format: A4 tax invoice if
    // set, else the native 80mm thermal receipt (default).
    if (getAssignedFormat('restaurantBill') === 'a4-tax-invoice') {
      // Inline uploaded logo/signature/QR (auth-only URLs) → data URIs for print.
      const eb = await getPrintBrandingInlined();
      const tenantH = await getTenantHeaderInlined();
      const dinNumber = `DIN-${tableCode}`;
      const a4: InvoicePrintInput = {
        docType: 'TAX INVOICE',
        number: dinNumber,
        date: new Date().toISOString(),
        customerType: 'B2C',
        tenant: tenantH,
        parties: [{ label: 'Bill to', name: activeOrder.customerName || 'Walk-in' }],
        lines: activeOrder.lines.filter((l) => l.qty > 0).map((l) => ({
          description: l.name, qty: l.qty, rate: l.rate, taxRate: l.taxRate,
          total: round2(l.qty * l.rate * (1 + l.taxRate / 100)),
        })),
        subTotal: round2(totals.subtotal),
        totalDiscount: 0,
        taxableAmount: round2(totals.subtotal),
        cgstAmount: round2(totals.tax / 2),
        sgstAmount: round2(totals.tax / 2),
        igstAmount: 0,
        roundOff: round2(totals.grand - (totals.subtotal + totals.tax)),
        totalAmount: totals.grand,
        // Branding: bank + signatory + always-present inline QR.
        bank: (eb.bankName || eb.bankAccountNo) ? { name: eb.bankName, accountName: eb.bankAccountName, accountNo: eb.bankAccountNo, ifsc: eb.bankIfsc, branch: eb.bankBranch } : undefined,
        signatureUrl: eb.signatureUrl,
        qrUrl: printQrUrl(eb.qrUrl, ['TAX INVOICE', dinNumber, activeOrder.customerName || 'Walk-in', 'Amt:' + totals.grand, tenant.gstin]),
      };
      printInvoiceByFormat('a4-tax-invoice', a4);
    } else {
      void printReceipt({
        table: tables.find((t) => String(t.restaurantTableId ?? t.id) === activeTableId),
        order: activeOrder,
        totals,
      });
    }
  }

  async function closeTable(tid: string | null = activeTableId) {
    if (!tid) return;
    setTableStatus((s) => ({ ...s, [tid]: 'Available' }));
    setOrders((cur) => {
      const next = { ...cur };
      delete next[tid];
      return next;
    });
    setMinimized((m) => { const n = { ...m }; delete n[tid]; return n; });
    // If we closed the ACTIVE table, fall back to another open one so the
    // editor doesn't go blank while other tables are still open.
    setActiveTableId((cur) => {
      if (cur !== tid) return cur;
      const remaining = Object.keys(orders).filter((k) => k !== tid);
      return remaining[0] ?? null;
    });
    toast.success('Table closed', 'Available for the next guest.');
  }

  return (
    <div className="-m-6 flex h-[calc(100vh-3.5rem)] flex-col gap-3 p-[3px]">
      {/* TOP: table grid (left) + active-table editor (right) */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-[1fr_400px] lg:grid-rows-[minmax(0,1fr)]">
      <div className="flex min-h-0 flex-col gap-3">
        {/* KPI strip */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          <StatusKpi label="Available" value={counts.Available} cls="text-success" />
          <StatusKpi label="Occupied"  value={counts.Occupied}  cls="text-warning" />
          <StatusKpi label="Reserved"  value={counts.Reserved}  cls="text-info" />
          <StatusKpi label="Billed"    value={counts.Billed}    cls="text-primary" />
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Reload
            </Button>
          </div>
        </div>

        {err && <Alert variant="danger">{err}</Alert>}

        {/* Tables grouped by zone */}
        <div className="flex-1 overflow-y-auto pr-1">
          {Object.keys(tablesByZone).length === 0 ? (
            <Card>
              <div className="p-6 text-sm text-content-muted">
                No tables yet. Add tables under <em>Restaurant → Tables</em> first.
              </div>
            </Card>
          ) : (
            Object.entries(tablesByZone).map(([zone, list]) => (
              <div key={zone} className="mb-4">
                <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-content-muted">
                  {zone} · {list.length} table(s)
                </h3>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                  {list.map((t) => {
                    const id = String(t.restaurantTableId ?? t.id ?? '');
                    const st = tableStatus[id] ?? 'Available';
                    const order = orders[id];
                    const isActive = activeTableId === id;
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => selectTable(t)}
                        className={`relative flex h-[110px] flex-col items-stretch overflow-hidden rounded-lg border-2 p-2 text-left transition ${
                          isActive ? 'border-primary' : statusBorder(st)
                        } ${statusBg(st)}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-base font-semibold text-content-primary">
                            {t.name ?? t.code ?? id.slice(0, 6)}
                          </span>
                          <span className="text-[10px] text-content-muted">
                            <Users size={10} className="inline" /> {Number(t.capacity ?? 4)}
                          </span>
                        </div>
                        <span className={`text-[11px] ${statusText(st)} mt-1 capitalize`}>{st}</span>
                        {order && order.lines.length > 0 && (
                          <span className="mt-auto text-[11px] text-content-muted">
                            {order.lines.reduce((n, l) => n + l.qty, 0)} items · ₹{inr(
                              Math.round(
                                order.lines.reduce(
                                  (s, l) => s + l.qty * l.rate * (1 + l.taxRate / 100),
                                  0,
                                ),
                              ),
                            )}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right pane: order */}
      <div className="flex min-h-0 flex-col gap-3">
        {!activeTableId ? (
          <Card className="flex flex-1 items-center justify-center">
            <div className="p-6 text-center text-sm text-content-muted">
              <Coffee className="mx-auto mb-2 h-8 w-8 opacity-40" />
              Pick a table to take an order.
            </div>
          </Card>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center gap-2 rounded-xl border border-border bg-surface-card px-4 py-3">
              <div className="min-w-0 flex-1">
                <h2 className="text-base font-semibold text-content-primary">
                  {tables.find((t) => String(t.restaurantTableId ?? t.id) === activeTableId)?.name ?? `Table ${activeTableId.slice(0, 6)}`}
                </h2>
                <p className="text-xs text-content-muted">
                  {totals.items} item(s) · ₹ {inr(totals.grand)}
                </p>
              </div>
              <button
                type="button"
                className="text-content-muted hover:text-danger"
                onClick={() => {
                  // Set this table aside — keep its order open (it drops to a
                  // card above) and switch the editor to another open table.
                  const others = Object.keys(orders).filter((k) => k !== activeTableId);
                  setActiveTableId(others[0] ?? null);
                }}
                aria-label="Set aside"
                title="Set aside — keeps the order open as a card"
              >
                <X size={18} />
              </button>
            </div>

            {/* Order type + guests */}
            <div className="rounded-xl border border-border bg-surface-card p-3">
              <div className="grid grid-cols-3 gap-2">
                <TypeChip mode="DineIn"   active={activeOrder!.type === 'DineIn'}   onClick={() => updateOrder({ type: 'DineIn' })}   icon={<Coffee size={14} />} />
                <TypeChip mode="Takeaway" active={activeOrder!.type === 'Takeaway'} onClick={() => updateOrder({ type: 'Takeaway' })} icon={<ShoppingBag size={14} />} />
                <TypeChip mode="Delivery" active={activeOrder!.type === 'Delivery'} onClick={() => updateOrder({ type: 'Delivery' })} icon={<Bike size={14} />} />
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs">
                <Users size={12} />
                <span className="text-content-muted">Guests</span>
                <input
                  type="number"
                  className="h-7 w-16 rounded-md border border-border bg-surface-card px-2 text-right text-sm outline-none focus:border-primary"
                  value={activeOrder!.guests}
                  min={1}
                  onChange={(e) => updateOrder({ guests: Math.max(1, Number(e.target.value) || 1) })}
                />
                <input
                  className="h-7 flex-1 rounded-md border border-border bg-surface-card px-2 text-sm outline-none focus:border-primary"
                  value={activeOrder!.customerName ?? ''}
                  onChange={(e) => updateOrder({ customerName: e.target.value })}
                  placeholder="Customer name (optional)"
                />
              </div>
            </div>

            {/* Menu picker — plain div (not Card) so the flex-col scroll chain
                isn't broken by Card's inner padding wrapper. `basis-0` so it
                SHARES the pane height with the order card below instead of
                eating all the slack and pushing KOT/Bill off-screen. */}
            <div className="flex min-h-0 flex-1 basis-0 flex-col rounded-xl border border-border bg-surface-card p-3">
              <div className="mb-2 flex flex-wrap gap-1">
                <CatChip active={activeCat === 'all'} onClick={() => setActiveCat('all')} label="All" />
                {categories.map((c) => {
                  const id = String(c.menuCategoryId ?? c.id ?? '');
                  return (
                    <CatChip
                      key={id}
                      active={activeCat === id}
                      onClick={() => setActiveCat(id)}
                      label={String(c.name ?? id.slice(0, 6))}
                    />
                  );
                })}
              </div>
              <div className="grid min-h-0 flex-1 grid-cols-2 content-start gap-2 overflow-y-auto sm:grid-cols-3">
                {menuItems
                  .filter((mi) =>
                    activeCat === 'all'
                      ? true
                      : String(mi.menuCategoryId ?? '') === activeCat,
                  )
                  .slice(0, 60)
                  .map((mi) => (
                    <button
                      key={String(mi.menuItemId ?? mi.id)}
                      type="button"
                      onClick={() => addItem(mi)}
                      className="flex flex-col overflow-hidden rounded-md border border-border bg-surface-card text-left hover:border-primary"
                    >
                      <div className="h-16 w-full overflow-hidden bg-surface-muted">
                        {mi.imageUrl ? (
                          <AuthImg src={String(mi.imageUrl)} className="h-full w-full object-cover" />
                        ) : (
                          <div className="grid h-full w-full place-items-center text-content-muted"><Package size={18} /></div>
                        )}
                      </div>
                      <div className="flex flex-1 flex-col p-2">
                        <span className="line-clamp-2 flex-1 text-xs font-medium">{mi.name ?? mi.code}</span>
                        <span className="mt-1 text-sm font-semibold text-primary">₹ {inr(Number(mi.price ?? mi.sellingPrice ?? 0))}</span>
                      </div>
                    </button>
                  ))}
                {menuItems.length === 0 && (
                  <div className="col-span-full p-4 text-xs text-content-muted">
                    No menu items yet — seed the menu under <em>Restaurant → Menu Items</em>.
                  </div>
                )}
              </div>
            </div>

            {/* Order lines + actions — shrink-0 so the KOT/Bill buttons stay
                visible; the line list inside scrolls (max-h-[26vh]). */}
            <Card padded={false} className="flex shrink-0 flex-col p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wider text-content-muted">
                  Order{activeOrder!.lines.length ? ` · ${activeOrder!.lines.length} line(s)` : ''}
                </span>
                {activeOrder!.lines.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setOrderFull(true)}
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    <Maximize2 size={12} /> Full screen
                  </button>
                )}
              </div>
              {/* Scrollable so a long order never pushes the menu/actions off-screen. */}
              <div className="-mx-1 max-h-[26vh] overflow-y-auto px-1">
                <OrderLines lines={activeOrder!.lines} onBump={bumpLine} onRemove={removeLine} />
              </div>
              <div className="mt-2 flex items-baseline justify-between border-t border-border pt-2 text-sm">
                <span className="text-content-muted">Total</span>
                <span className="text-base font-semibold tabular-nums">₹ {inr(totals.grand)}</span>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                <Button variant="outline" onClick={sendToKot} disabled={!activeOrder!.lines.some((l) => l.unsent)}>
                  <Send className="h-4 w-4" /> KOT
                </Button>
                <Button onClick={printBill} disabled={!activeOrder!.lines.length}>
                  <Printer className="h-4 w-4" /> Bill
                </Button>
                <Button variant="outline" onClick={() => closeTable()}>
                  <X className="h-4 w-4" /> Close
                </Button>
              </div>
            </Card>
          </>
        )}
      </div>
      </div>

      {/* FOOTER: open-order cards BELOW the table grid — the "open tickets" bar.
          Every table you open shows here with its items (collapse with the
          chevron); tap a card to edit that order in the panel above. Scrolls
          horizontally so 20+ tables fit. */}
      {Object.keys(orders).length > 0 && (
        <div className="shrink-0 border-t border-border pt-2">
          <div className="mb-1 px-1 text-[11px] font-medium uppercase tracking-wider text-content-muted">
            Open orders · {Object.keys(orders).length}
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {Object.keys(orders).map((id) => {
              const o = orders[id];
              const ot = orderTotals(o);
              const row = tables.find((t) => String(t.restaurantTableId ?? t.id) === id);
              const label = String(row?.name ?? row?.code ?? `Table ${id.slice(0, 6)}`);
              const collapsed = !!minimized[id];
              const st = tableStatus[id] ?? 'Occupied';
              const active = id === activeTableId;
              return (
                <div key={id} className={`flex w-60 shrink-0 flex-col rounded-xl border bg-surface-card ${active ? 'border-primary ring-1 ring-primary' : 'border-border'}`}>
                  <div className="flex items-center gap-1.5 px-2.5 py-2">
                    <button type="button" onClick={() => toggleMin(id)} className="text-content-muted hover:text-content-primary" aria-label={collapsed ? 'Expand' : 'Minimize'}>
                      {collapsed ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                    </button>
                    <button type="button" onClick={() => setActiveTableId(id)} className="min-w-0 flex-1 text-left">
                      <div className="truncate text-sm font-semibold text-content-primary">{label}</div>
                      <div className="truncate text-[11px] text-content-muted">{ot.items} item(s) · ₹ {inr(ot.grand)} · {st}</div>
                    </button>
                    <button type="button" onClick={() => closeTable(id)} className="text-content-muted hover:text-danger" aria-label="Close table"><X size={14} /></button>
                  </div>
                  {!collapsed && (
                    o.lines.length ? (
                      <ul className="max-h-32 space-y-1 overflow-y-auto border-t border-border px-2.5 py-1.5 text-[12px]">
                        {o.lines.map((l, i) => (
                          <li key={i} className="flex items-baseline justify-between gap-2">
                            <span className="min-w-0 flex-1 truncate"><span className="font-semibold">{l.qty}×</span> {l.name}{l.unsent && <span className="ml-1 text-[10px] text-warning">•</span>}</span>
                            <span className="tabular-nums text-content-muted">₹ {inr(Math.round(l.qty * l.rate * (1 + l.taxRate / 100)))}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="border-t border-border px-2.5 py-1.5 text-[11px] italic text-content-muted">No items yet.</div>
                    )
                  )}
                  <button type="button" onClick={() => setActiveTableId(id)} className={`mt-auto border-t border-border px-2.5 py-1.5 text-left text-[11px] font-medium ${active ? 'text-primary' : 'text-content-secondary hover:text-primary'}`}>
                    {active ? '● Editing' : 'Open'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Full-screen order overlay — big, scrollable, properly aligned. */}
      {orderFull && activeOrder && (
        <Overlay onClose={() => setOrderFull(false)}>
          <div className="flex h-full flex-col">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-content-primary">
                  {tables.find((t) => String(t.restaurantTableId ?? t.id) === activeTableId)?.name ?? `Table ${activeTableId?.slice(0, 6)}`} — Order
                </h3>
                <p className="text-[11px] text-content-muted">
                  {totals.items} item(s) · {activeOrder.lines.length} line(s) · {activeOrder.customerName || 'Walk-in'}
                </p>
              </div>
              <button onClick={() => setOrderFull(false)} className="grid h-8 w-8 place-items-center rounded hover:bg-surface-card-hover" aria-label="Close"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <OrderLines lines={activeOrder.lines} onBump={bumpLine} onRemove={removeLine} large />
            </div>
            <div className="flex items-center justify-between gap-4 border-t border-border p-4">
              <div className="text-lg font-semibold text-content-primary">Total ₹ {inr(totals.grand)}</div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={sendToKot} disabled={!activeOrder.lines.some((l) => l.unsent)}><Send className="h-4 w-4" /> KOT</Button>
                <Button onClick={() => { void printBill(); }} disabled={!activeOrder.lines.length}><Printer className="h-4 w-4" /> Bill</Button>
                <Button variant="outline" onClick={() => setOrderFull(false)}>Done</Button>
              </div>
            </div>
          </div>
        </Overlay>
      )}
    </div>
  );
}

/** Scrollable, reusable order-line list (side panel + full-screen modal). */
function OrderLines({ lines, onBump, onRemove, large }: {
  lines: OrderLine[];
  onBump: (i: number, d: number) => void;
  onRemove: (i: number) => void;
  large?: boolean;
}) {
  if (!lines.length) return <div className="py-2 text-xs text-content-muted">Tap an item above to add it.</div>;
  return (
    <ul className="divide-y divide-border/40">
      {lines.map((l, i) => (
        <li key={`${l.menuItemId}-${i}`} className={`flex items-center gap-2 ${large ? 'py-2.5' : 'py-1.5'}`}>
          <div className={`min-w-0 flex-1 ${l.unsent ? '' : 'opacity-60'}`}>
            <div className={`truncate ${large ? 'text-base' : 'text-sm'} ${l.unsent ? 'font-medium' : 'line-through'}`}>{l.name}</div>
            <div className="text-[10px] text-content-muted">₹ {inr(l.rate)} · GST {l.taxRate}%{l.unsent ? '' : ' · sent'}</div>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" className={`grid place-items-center rounded border border-border hover:border-primary ${large ? 'h-8 w-8' : 'h-6 w-6'}`} onClick={() => onBump(i, -1)} aria-label="Decrease"><Minus size={large ? 14 : 11} /></button>
            <span className={`text-center font-medium tabular-nums ${large ? 'w-9 text-base' : 'w-7 text-sm'}`}>{l.qty}</span>
            <button type="button" className={`grid place-items-center rounded border border-border hover:border-primary ${large ? 'h-8 w-8' : 'h-6 w-6'}`} onClick={() => onBump(i, +1)} aria-label="Increase"><Plus size={large ? 14 : 11} /></button>
            <button type="button" className="ml-1 text-content-muted hover:text-danger" onClick={() => onRemove(i)} aria-label="Remove"><Trash2 size={large ? 15 : 12} /></button>
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Lightweight modal overlay for the full-screen order view. */
function Overlay({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3" onClick={onClose}>
      <div className="h-[92vh] w-[96vw] max-w-3xl overflow-hidden rounded-xl border border-border bg-surface-card shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  UI bits                                                             */
/* -------------------------------------------------------------------- */

function StatusKpi({ label, value, cls }: { label: string; value: number; cls: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface-card px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-content-muted">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}

function CatChip({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-7 shrink-0 rounded-full border px-2.5 text-[11px] ${
        active
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-border bg-surface-card text-content-secondary hover:border-primary'
      }`}
    >
      {label}
    </button>
  );
}

function TypeChip({
  mode, active, onClick, icon,
}: { mode: OrderType; active: boolean; onClick: () => void; icon: React.ReactNode }) {
  const labels: Record<OrderType, string> = { DineIn: 'Dine-in', Takeaway: 'Takeaway', Delivery: 'Delivery' };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-9 items-center justify-center gap-1.5 rounded-md border text-xs ${
        active ? 'border-primary bg-primary text-white' : 'border-border bg-surface-card hover:border-primary'
      }`}
    >
      {icon} {labels[mode]}
    </button>
  );
}

/* -------------------------------------------------------------------- */
/*  Helpers                                                             */
/* -------------------------------------------------------------------- */

function pickList<T>(r: { data: unknown }): T[] {
  const d = r.data;
  if (Array.isArray(d)) return d as T[];
  if (d && typeof d === 'object') {
    const obj = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
    if (Array.isArray(obj.items)) return obj.items as T[];
    if (Array.isArray(obj.data)) return obj.data as T[];
    if (obj.data && Array.isArray((obj.data as { items?: unknown[] }).items)) {
      return (obj.data as { items: unknown[] }).items as T[];
    }
  }
  return [];
}

function normaliseStatus(raw: string): TableStatus {
  const s = raw.trim().toLowerCase();
  if (s === 'occupied') return 'Occupied';
  if (s === 'reserved') return 'Reserved';
  if (s === 'billed') return 'Billed';
  if (s === 'closed') return 'Closed';
  return 'Available';
}

function statusBg(s: TableStatus): string {
  if (s === 'Occupied') return 'bg-warning/10';
  if (s === 'Reserved') return 'bg-info/10';
  if (s === 'Billed') return 'bg-primary/10';
  if (s === 'Closed') return 'bg-content-muted/10';
  return 'bg-success/10';
}

function statusBorder(s: TableStatus): string {
  if (s === 'Occupied') return 'border-warning/40';
  if (s === 'Reserved') return 'border-info/40';
  if (s === 'Billed') return 'border-primary/40';
  if (s === 'Closed') return 'border-border';
  return 'border-success/40';
}

function statusText(s: TableStatus): string {
  if (s === 'Occupied') return 'text-warning';
  if (s === 'Reserved') return 'text-info';
  if (s === 'Billed') return 'text-primary';
  if (s === 'Closed') return 'text-content-muted';
  return 'text-success';
}

function inr(n: number): string {
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

interface ReceiptCtx {
  table: RestaurantTable | undefined;
  order: Order;
  totals: { subtotal: number; tax: number; grand: number; items: number };
}

async function printReceipt({ table, order, totals }: ReceiptCtx) {
  const win = window.open('', '_blank', 'width=320,height=600');
  if (!win) return;
  // window.open ran synchronously (no popup block); now inline the branding.
  const t = await getTenantHeaderInlined();
  const eb = await getPrintBrandingInlined();
  const billQr = printQrUrl(eb.qrUrl, [t.name, table?.name ?? 'Table', 'Amt:' + totals.grand]);
  const initials = (t.name || 'KSS').trim().split(/\s+/).filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase() || 'KS';
  const logoBlock = t.logoUrl
    ? `<img src="${escapeHtml(t.logoUrl)}" alt="" style="display:block;margin:0 auto 4px;max-height:48px;max-width:160px;object-fit:contain" onerror="this.style.display='none'"/>`
    : `<div style="width:42px;height:42px;border-radius:8px;background:#0f766e;color:#fff;font:700 18px/42px monospace;text-align:center;margin:0 auto 4px">${escapeHtml(initials)}</div>`;
  const lines = order.lines
    .map(
      (l) =>
        `<tr><td>${escapeHtml(l.name)}</td><td style="text-align:right">${l.qty}</td><td style="text-align:right">${inr(
          l.qty * l.rate * (1 + l.taxRate / 100),
        )}</td></tr>`,
    )
    .join('');
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Bill</title>
<style>
  body { font-family: monospace; padding: 8px; width: 280px; }
  h1 { font-size: 14px; margin: 0 0 4px; text-align: center; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th, td { padding: 2px 0; }
  thead { border-bottom: 1px dashed #999; }
  tfoot { border-top: 1px dashed #999; }
  .total { font-weight: 700; font-size: 13px; }
  .meta { font-size: 10px; color: #666; text-align: center; margin-bottom: 4px; }
</style></head><body>
${logoBlock}
<h1>${escapeHtml(t.name)} · ${escapeHtml(table?.name ?? 'Table')}</h1>
<div class="meta">${new Date().toLocaleString('en-IN')} · ${order.type}${
    order.customerName ? ` · ${escapeHtml(order.customerName)}` : ''
  }</div>
<table>
  <thead><tr><th align="left">Item</th><th align="right">Qty</th><th align="right">Amt</th></tr></thead>
  <tbody>${lines}</tbody>
  <tfoot>
    <tr><td colspan="2">Subtotal</td><td align="right">₹ ${inr(totals.subtotal)}</td></tr>
    <tr><td colspan="2">Tax</td><td align="right">₹ ${inr(totals.tax)}</td></tr>
    <tr><td colspan="2">Total</td><td align="right" class="total">₹ ${inr(totals.grand)}</td></tr>
  </tfoot>
</table>
<div style="text-align:center;margin-top:8px"><img src="${billQr}" alt="" style="width:96px;height:96px" onerror="this.style.display='none'"/><div class="meta">Scan to pay / verify</div></div>
${eb.signatureUrl ? `<div style="text-align:center;margin-top:6px"><img src="${escapeHtml(eb.signatureUrl)}" alt="" style="max-height:36px" onerror="this.style.display='none'"/></div>` : ''}
<div class="meta" style="margin-top:6px">Authorised Signatory · For ${escapeHtml(t.name)}</div>
<div class="meta" style="margin-top:8px">Thank you. Visit again!</div>
<script>window.onload=()=>window.print();</script>
</body></html>`);
  win.document.close();
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
